import { computed, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { generateUid } from '@gametailors/web-core/misc';
import { LpMissionInfo, LpRidLocationStop } from '../../../typings/schema';
import { LEERPLUS } from '../rid-game.utils';
import { RidCalendarService } from './rid-calendar.service';

@Injectable()
export class RidHomeworkService {
  private readonly $lpApiService = inject(LEERPLUS.apiToken);
  private readonly $ridCalendarService = inject(RidCalendarService);

  public readonly currentStop: WritableSignal<LpRidLocationStop | undefined> = signal(undefined);

  /** A new stop can be started today: none was started yet today, and there's homework left for this week. */
  public readonly canStartNewStop = computed(() => !this.hasLinkedToday() && this.pendingHomeworkStops().length > 0);

  /** The homework stops that aren't linked to a location stop yet, in order. */
  private readonly pendingHomeworkStops = computed(() => {
    const state = this.$lpApiService.state();
    const linkedHomeworkStopIds = new Set(state.homeworkLinks.stops.map((link) => link.homeworkStopId));
    return state.homework.stops.filter((s) => !linkedHomeworkStopIds.has(s.id));
  });

  private readonly hasLinkedToday = computed(() =>
    this.$lpApiService.state().homeworkLinks.stops.some((link) => link.linkedDate === this.$ridCalendarService.today()),
  );

  /** The date (YYYY-MM-DD) the stop got its homework, so the stops read as a log book. Unset while it has none. */
  public stopDate(stop: LpRidLocationStop): string | undefined {
    return this.findLink(stop)?.linkedDate;
  }

  /** It was started today and isn't finished yet. Stops left unfinished on earlier days don't count, so they don't call the player back. */
  public isStopToDoToday(stop: LpRidLocationStop): boolean {
    return this.findLink(stop)?.linkedDate === this.$ridCalendarService.today() && !this.isStopFinished(stop);
  }

  /** It's linked to homework. */
  public isStopStarted(stop: LpRidLocationStop): boolean {
    return !!this.findLink(stop);
  }

  /** All its assignments are finished. Until then it stays open to play, on any day. */
  public isStopFinished(stop: LpRidLocationStop): boolean {
    const finished = this.finishedPerAssignment(stop);
    return finished.length > 0 && finished.every(Boolean);
  }

  public isStopInProgress(stop: LpRidLocationStop): boolean {
    return this.isStopStarted(stop) && !this.isStopFinished(stop);
  }

  /** The stops sorted in the order they were started, the ones not started yet last, so progress fills up from the left. */
  public sortStopsByStartOrder(stops: LpRidLocationStop[]): LpRidLocationStop[] {
    return [...stops].sort((a, b) => this.positionInStartOrder(a) - this.positionInStartOrder(b));
  }

  /** The stop's position in the order the stops were started (0 for the first stop ever started); Infinity while it isn't started. */
  public positionInStartOrder(stop: LpRidLocationStop): number {
    // links are only ever appended, so their order is the order the stops were started in
    const index = this.$lpApiService.state().homeworkLinks.stops.findIndex((link) => link.locationStopUid === stop.uid);
    return index === -1 ? Infinity : index;
  }

  public goToStop(stop: LpRidLocationStop) {
    const mission = this.getTemplateMission();
    mission.name = stop.name;
    this.$lpApiService.setVariable('var_zUKliagW6jgGX50jJsHow', stop.background.uid);

    if (!this.findLink(stop)) {
      this.linkNewHomeworkStop(stop);
    }

    this.currentStop.set(stop);
    this.$lpApiService.startMission(mission, true, { dataType: 'FadeTransition', uid: generateUid() });
  }

  private linkNewHomeworkStop(stop: LpRidLocationStop) {
    const chosen = this.pendingHomeworkStops().at(0);
    if (!chosen) throw new Error('No pending homework stop to link');

    this.$lpApiService.state.update((s) => ({
      ...s,
      homeworkLinks: {
        ...s.homeworkLinks,
        stops: [
          ...s.homeworkLinks.stops,
          {
            homeworkStopId: chosen.id,
            locationStopUid: stop.uid,
            linkedDate: this.$ridCalendarService.today(),
            assignmentActivityUids: {},
          },
        ],
      },
    }));
  }

  private findLink(stop: LpRidLocationStop) {
    return this.$lpApiService.state().homeworkLinks.stops.find((link) => link.locationStopUid === stop.uid);
  }

  /** Whether each assignment of the stop's homework is finished, in order. Empty while the stop has no homework: it isn't started yet, or its homework is no longer loaded. */
  public finishedPerAssignment(stop: LpRidLocationStop): boolean[] {
    const link = this.findLink(stop);
    if (!link) return [];

    const state = this.$lpApiService.state();
    const homeworkStop = state.homework.stops.find((s) => s.id === link.homeworkStopId);
    if (!homeworkStop) return [];

    return homeworkStop.assignments.map((a) => {
      const activityUid = link.assignmentActivityUids[a.config.uid];
      return !!activityUid && state.activityProgress.some((p) => p.uid === activityUid && !!p.finished_timestamp);
    });
  }

  private getTemplateMission(): LpMissionInfo {
    const templateMission = this.$lpApiService
      .getInstancesByType('MissionInfo')
      .find((m) => m.uid === 'di_missioninfo_pm56vZV7FGiJTvwbfJDMx');
    if (!templateMission) throw new Error(`Template mission 'di_missioninfo_pm56vZV7FGiJTvwbfJDMx' not found`);

    const mission = structuredClone(templateMission);
    const hasAssignmentsComponent = mission.mission.activities
      .filter((a) => a.dataType === 'CanvasActivity')
      .flatMap((a) => a.canvasElements.flatMap((e) => e.components))
      .some((c) => c.dataType === 'RidAssignmentsCanvasComponent');
    if (!hasAssignmentsComponent) throw new Error(`RidAssignmentsCanvasComponent not found in template mission`);

    return mission;
  }
}
